
import { useState, useEffect } from "react";
import { useCoachProfile } from "./useCoachProfile";
import { useSupervisionRequests } from "./useSupervisionRequests";
import { useActiveTrainees } from "./useActiveTrainees";

export const useCoachManagement = () => {
  const [loading, setLoading] = useState(true);
  const { coachProfile, isCoach, checkCoachStatus, becomeCoach, deleteCoachProfile } = useCoachProfile();
  const { supervisionRequests, fetchSupervisionRequests } = useSupervisionRequests();
  const { activeTrainees, fetchActiveTrainees } = useActiveTrainees();

  useEffect(() => {
    const initializeCoachData = async () => {
      setLoading(true);
      try {
        const { isCoach: userIsCoach, coachProfile: profile } = await checkCoachStatus();
        
        if (userIsCoach && profile) {
          await Promise.all([
            fetchSupervisionRequests(profile.id),
            fetchActiveTrainees(profile.id)
          ]);
        }
      } catch (error) {
        console.error("Erreur lors de l'initialisation:", error);
      } finally {
        setLoading(false);
      }
    };

    initializeCoachData();
  }, [checkCoachStatus, fetchSupervisionRequests, fetchActiveTrainees]);

  const handleBecomeCoach = async (coachData: any) => {
    setLoading(true);
    try {
      const coach = await becomeCoach(coachData);
      
      await Promise.all([
        fetchSupervisionRequests(coach.id),
        fetchActiveTrainees(coach.id)
      ]);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refreshSupervisionRequests = () => {
    if (coachProfile) {
      return fetchSupervisionRequests(coachProfile.id);
    }
    return Promise.resolve();
  };

  const refreshActiveTrainees = () => {
    if (coachProfile) {
      return fetchActiveTrainees(coachProfile.id);
    }
    return Promise.resolve();
  };

  const handleDeleteCoachProfile = async () => {
    setLoading(true);
    try {
      await deleteCoachProfile();
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    isCoach,
    coachProfile,
    supervisionRequests,
    activeTrainees,
    loading,
    becomeCoach: handleBecomeCoach,
    deleteCoachProfile: handleDeleteCoachProfile,
    fetchSupervisionRequests: refreshSupervisionRequests,
    fetchActiveTrainees: refreshActiveTrainees,
  };
};
